import React, { useEffect, useMemo, useState } from "react";
import { Trash, Plus, X, PencilSimple, ArrowsLeftRight } from "@phosphor-icons/react";
import { api, fileUrl } from "../../lib/api";
import ImageUploader from "../../components/admin/ImageUploader";
import { toast } from "sonner";

const EMPTY = { service_slug: "", title: "", caption: "", before_image: "", after_image: "", order: 0 };

export default function AdminBeforeAfter() {
  const [services, setServices] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [s, p] = await Promise.all([
      api.get("/services"),
      api.get("/before-after"),
    ]);
    setServices(s.data);
    setPairs(p.data);
  };
  useEffect(() => { load(); }, []);

  const serviceTitle = (slug) => services.find((s) => s.slug === slug)?.title || slug;

  const visible = useMemo(() => filter === "all" ? pairs : pairs.filter((p) => p.service_slug === filter), [pairs, filter]);

  const openNew = () => {
    setEditing("new");
    setForm({ ...EMPTY, service_slug: services[0]?.slug || "" });
  };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.service_slug) return toast.error("Pick a service");
    if (!form.before_image || !form.after_image) return toast.error("Upload both before and after images");
    try {
      if (editing === "new") await api.post("/before-after", form);
      else await api.put(`/before-after/${editing}`, form);
      toast.success("Saved");
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this pair?")) return;
    await api.delete(`/before-after/${id}`);
    load();
  };

  return (
    <div className="p-8 md:p-10" data-testid="admin-before-after">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2">Showcase</div>
          <h1 className="font-display text-5xl uppercase text-white leading-none">Before & After</h1>
          <p className="text-white/60 mt-2 text-sm">Project transformation pairs shown on each service detail page.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="new-pair-btn">
          <Plus size={18} weight="bold" /> New Pair
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="ba-filters">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 ${filter === "all" ? "bg-atc-red text-white border-atc-red" : "bg-transparent text-white/70 border-[#333] hover:border-atc-red"}`}
          data-testid="ba-filter-all"
        >All</button>
        {services.map((s) => (
          <button
            key={s.slug}
            onClick={() => setFilter(s.slug)}
            className={`px-4 py-2 font-heading uppercase tracking-widest text-xs border-2 ${filter === s.slug ? "bg-atc-red text-white border-atc-red" : "bg-transparent text-white/70 border-[#333] hover:border-atc-red"}`}
            data-testid={`ba-filter-${s.slug}`}
          >{s.title}</button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="border-2 border-dashed border-[#333] p-16 text-center text-white/50">
          No pairs yet — click <span className="text-atc-red">New Pair</span> to add one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visible.map((p) => (
            <div key={p.id} className="admin-card group" data-testid={`pair-row-${p.id}`}>
              <div className="grid grid-cols-2 gap-px bg-[#333]">
                <div className="aspect-[4/3] bg-atc-black overflow-hidden relative">
                  <img src={fileUrl(p.before_image)} alt="Before" className="w-full h-full object-cover grayscale" />
                  <span className="absolute top-2 left-2 bg-atc-black/80 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1">Before</span>
                </div>
                <div className="aspect-[4/3] bg-atc-black overflow-hidden relative">
                  <img src={fileUrl(p.after_image)} alt="After" className="w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 bg-atc-red text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1">After</span>
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="overline mb-1">{serviceTitle(p.service_slug)}</div>
                  <div className="font-heading uppercase text-white truncate">{p.title || "Untitled pair"}</div>
                  {p.caption && <div className="text-white/50 text-xs mt-1 line-clamp-2">{p.caption}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(p)} className="border border-[#333] hover:border-atc-red p-2" data-testid={`edit-pair-${p.id}`}>
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button onClick={() => del(p.id)} className="border border-[#333] hover:border-atc-red p-2" data-testid={`delete-pair-${p.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-atc-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="pair-modal">
          <div className="bg-[#1f1f1f] border-2 border-atc-red max-w-3xl w-full p-8 my-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <ArrowsLeftRight size={28} weight="bold" className="text-atc-red" />
                <h2 className="font-display text-3xl uppercase text-white">{editing === "new" ? "New pair" : "Edit pair"}</h2>
              </div>
              <button onClick={close} className="text-white/60 hover:text-atc-red"><X size={24} weight="bold" /></button>
            </div>
            <div className="space-y-5">
              <div>
                <div className="admin-label">Service *</div>
                <select className="admin-input" value={form.service_slug} onChange={(e) => setForm({ ...form, service_slug: e.target.value })} data-testid="pair-service">
                  <option value="">Select a service…</option>
                  {services.map((s) => (<option key={s.slug} value={s.slug}>{s.title}</option>))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <ImageUploader value={form.before_image} onChange={(p) => setForm({ ...form, before_image: p })} folder="before-after" label="Before image *" />
                <ImageUploader value={form.after_image} onChange={(p) => setForm({ ...form, after_image: p })} folder="before-after" label="After image *" />
              </div>
              <div>
                <div className="admin-label">Title</div>
                <input className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Glen Rock Composite Deck" data-testid="pair-title" />
              </div>
              <div>
                <div className="admin-label">Caption</div>
                <textarea rows={2} className="admin-input" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} placeholder="Optional short description" data-testid="pair-caption" />
              </div>
              <div className="md:w-32">
                <div className="admin-label">Order</div>
                <input type="number" className="admin-input" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} data-testid="pair-order" />
              </div>
              <div className="flex gap-3 pt-4 border-t border-[#2a2a2a]">
                <button onClick={save} className="btn-primary" data-testid="save-pair-btn">Save Pair</button>
                <button onClick={close} className="btn-ghost-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
